import { useEffect, useMemo, useRef, useState } from "react";
import { api, absUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, Play, RefreshCcw, AlertTriangle } from "lucide-react";

// Pretty-print song-time seconds as m:ss.x
function fmtT(t) {
  if (t == null || isNaN(t)) return "—";
  const m = Math.floor(t / 60);
  const s = (t - m * 60).toFixed(2);
  return `${m}:${s.padStart(5, "0")}`;
}

const CONCEPT_LABEL = {
  horn: "🔊 Horn (beep beep)",
  wipers: "🌧️ Wipers (swish swish)",
  wheels: "🚌 Wheels (round and round)",
  doors: "🚪 Doors (open and shut)",
  babies: "👶 Babies (wah wah)",
  mommies: "🤫 Mommies (shh shh)",
  daddies: "👨 Daddies",
  people: "🧑‍🤝‍🧑 People (on the bus)",
};

/**
 * AnchorConfirmPanel — Step 2 of the user-confirmed restitch flow.
 * - Streams the bare Suno mp3 via /api/videos/{id}/audio (range support).
 * - Lists every anchor cluster (from /api/videos/{id}/anchors).
 * - Lets the user pick one cluster per concept (radio per concept).
 * - "Re-stitch v5" POSTs the picks to /api/videos/{id}/restitch and
 *   shows the verifier verdict on the new mp4.
 */
export default function AnchorConfirmPanel({ video, onRestitched }) {
  const [data, setData] = useState(null); // {concepts: {horn: [{first_word_t,...}], ...}}
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // picks[concept] = cluster_idx (within that concept's list)
  const [picks, setPicks] = useState({});
  const [restitching, setRestitching] = useState(false);
  const [result, setResult] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    api.get(`/videos/${video.id}/anchors`)
      .then((res) => {
        if (!alive) return;
        setData(res.data);
        // Pre-pick the FIRST cluster for each concept (current default)
        const initial = {};
        Object.keys(res.data.concepts || {}).forEach((c) => {
          initial[c] = 0;
        });
        setPicks(initial);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [video.id]);

  const audioUrl = useMemo(
    () => absUrl(`/api/videos/${video.id}/audio`),
    [video.id]
  );

  function jumpTo(t) {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = t;
    a.play().catch(() => {});
  }

  const concepts = data?.concepts || {};
  const orderedConcepts = useMemo(() => {
    return Object.entries(concepts).sort((a, b) => {
      const at = (a[1][0] || {}).first_word_t || 0;
      const bt = (b[1][0] || {}).first_word_t || 0;
      return at - bt;
    });
  }, [concepts]);

  async function onRestitch() {
    setRestitching(true);
    setResult(null);
    try {
      const overrides = {};
      Object.entries(picks).forEach(([concept, idx]) => {
        const c = (concepts[concept] || [])[idx];
        if (c) {
          overrides[concept] = {
            first: c.first_word_t,
            last: c.last_word_t,
          };
        }
      });
      const res = await api.post(`/videos/${video.id}/restitch`, {
        anchor_overrides: overrides,
        version_label: "v5",
      });
      setResult(res.data);
      if (onRestitched) onRestitched(res.data);
    } catch (e) {
      setResult({
        error: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRestitching(false);
    }
  }

  if (loading) {
    return (
      <div
        data-testid="anchor-confirm-panel"
        className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-3 text-sm text-stone-600"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading anchor clusters…
      </div>
    );
  }
  if (err) {
    return (
      <div
        data-testid="anchor-confirm-panel"
        className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-800"
      >
        <AlertTriangle className="mr-1 inline h-4 w-4" />
        Could not load anchors: {err}
      </div>
    );
  }

  return (
    <div
      data-testid="anchor-confirm-panel"
      className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold text-indigo-900">
          🎯 Anchor Confirm — pick the canonical cluster per concept
        </h3>
        <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100">
          v5 restitch · $0 paid spend
        </Badge>
      </div>
      <p className="mb-3 text-xs text-indigo-900/70">
        The pipeline auto-picks the first cluster per concept. Sometimes
        whisper hallucinates a long tail (e.g. 200+ "beep" tokens during a
        fadeout). Use the player below to confirm which cluster is the
        real onset, then click Re-stitch.
      </p>

      <audio
        ref={audioRef}
        src={audioUrl}
        controls
        preload="metadata"
        data-testid="anchor-audio-player"
        className="mb-4 w-full"
      />

      <div className="space-y-3" data-testid="anchor-cluster-list">
        {orderedConcepts.length === 0 ? (
          <div className="text-xs text-stone-600">
            No anchor clusters detected for this video.
          </div>
        ) : null}
        {orderedConcepts.map(([concept, clusters]) => (
          <fieldset
            key={concept}
            className="rounded-lg border border-stone-200 bg-white p-3"
            data-testid={`anchor-concept-${concept}`}
          >
            <legend className="px-1 text-xs font-bold text-stone-700">
              {CONCEPT_LABEL[concept] || concept}
              <span className="ml-2 font-normal text-stone-500">
                ({clusters.length} cluster{clusters.length === 1 ? "" : "s"})
              </span>
            </legend>
            <div className="grid gap-2">
              {clusters.map((c, idx) => {
                const checked = picks[concept] === idx;
                const isHallucinated = c.n_words > 30; // heuristic: 200x "beep"
                return (
                  <label
                    key={idx}
                    className={[
                      "flex cursor-pointer gap-3 rounded-md border px-3 py-2 text-xs",
                      checked
                        ? "border-indigo-400 bg-indigo-50"
                        : "border-stone-200 bg-stone-50 hover:border-stone-300",
                    ].join(" ")}
                    data-testid={`anchor-cluster-${concept}-${idx}`}
                  >
                    <input
                      type="radio"
                      name={`pick-${concept}`}
                      className="mt-0.5"
                      checked={checked}
                      onChange={() =>
                        setPicks((p) => ({ ...p, [concept]: idx }))
                      }
                      data-testid={`anchor-radio-${concept}-${idx}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="rounded bg-white px-1.5 py-0.5 text-[10px] text-stone-700 ring-1 ring-stone-200">
                          {fmtT(c.first_word_t)} → {fmtT(c.last_word_t)}
                        </code>
                        <span className="text-[10px] text-stone-500">
                          dur {c.duration?.toFixed(2)}s · {c.n_words}w
                        </span>
                        {isHallucinated ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                            ⚠ likely hallucination
                          </Badge>
                        ) : null}
                        {idx === 0 ? (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                            default
                          </Badge>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="ml-auto h-6 px-2 text-[10px]"
                          onClick={(e) => {
                            e.preventDefault();
                            jumpTo(c.first_word_t);
                          }}
                          data-testid={`anchor-jump-${concept}-${idx}`}
                        >
                          <Play className="mr-1 h-3 w-3" />
                          Jump
                        </Button>
                      </div>
                      <div className="mt-1 line-clamp-2 break-words font-mono text-[11px] text-stone-700">
                        {c.exact_span_text}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          onClick={onRestitch}
          disabled={restitching || orderedConcepts.length === 0}
          className="bg-indigo-600 text-white hover:bg-indigo-700"
          data-testid="anchor-restitch-button"
        >
          {restitching ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              Re-stitching v5… (~30-90s, no Suno/fal calls)
            </>
          ) : (
            <>
              <RefreshCcw className="mr-1 h-4 w-4" />
              Re-stitch v5 using my picks
            </>
          )}
        </Button>
        <span className="text-[11px] text-stone-500">
          Reuses cached Suno mp3, Hailuo 02 clips, nano-banana stills.
          Vision LLM verifier (~$0.01) runs on the new mp4.
        </span>
      </div>

      {result ? (
        <div
          data-testid="anchor-restitch-result"
          className={[
            "mt-4 rounded-lg border p-3 text-xs",
            result.error
              ? "border-rose-300 bg-rose-50 text-rose-900"
              : (result.content_verification?.passed
                  ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                  : "border-amber-300 bg-amber-50 text-amber-900"),
          ].join(" ")}
        >
          {result.error ? (
            <div>Error: {result.error}</div>
          ) : (
            <>
              <div className="font-bold">
                {result.content_verification?.passed ? (
                  <><Check className="mr-1 inline h-3 w-3" /> v{result.version} passed Vision verifier</>
                ) : (
                  <><AlertTriangle className="mr-1 inline h-3 w-3" /> v{result.version} did not pass</>
                )}
                {" — "}
                {result.content_verification?.total_checks ?? 0} checks,
                {" "}{result.content_verification?.pass_pct ?? 0}% pass
              </div>
              <div className="mt-1">
                Duration: {result.duration_sec?.toFixed(2)}s ·
                Size: {result.file_size_mb}MB ·
                Intro offset: {result.intro_offset?.toFixed(2)}s
              </div>
              <ul className="mt-2 space-y-0.5 font-mono">
                {(result.content_verification?.checks || []).map((c, i) => (
                  <li key={i}>
                    {c.is_match ? "✓" : "✗"} {c.anchor_word}{" "}
                    @audio_t={c.audio_t_song?.toFixed(2)}s →{" "}
                    sample={c.sample_t_video?.toFixed(2)}s
                    (conf {c.confidence?.toFixed(2)}):{" "}
                    {c.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
